/**
 * Eigen Rooster Widget (Epic 5, Story 5.1 — Rolgebaseerd dashboard, UX V7)
 *
 * Medewerker-sectie (altijd zichtbaar): compacte strook van het eigen
 * werkrooster deze week, doorklik naar "Mijn rooster" (V3). Hergebruikt de
 * Story 1.4-hooks (`useEmployeeSchedule`/`useCurrentWeekType`) en
 * `weekUtils` (dag-labels, effectieve-week-bepaling) — geen tweede
 * roosterweergave-implementatie (AD-9).
 */

import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { useEmployeeSchedule, useCurrentWeekType } from '../werkrooster/hooks/useWorkSchedules';
import { DAY_LABELS, getDayEntryForDay, hasWeekData, getDefaultWeek, WORKWEEK_DAYS } from '../werkrooster/utils/weekUtils';

const ORDERED_DAYS = WORKWEEK_DAYS;

export function EigenRoosterWidget() {
  const { user } = useAuth();
  const employeeId = user?.employeeId ?? undefined;

  const { data: scheduleData, isLoading, isError } = useEmployeeSchedule(employeeId ?? 0);
  const { data: weekData } = useCurrentWeekType();

  const schedule = scheduleData?.data;
  const currentWeekType = weekData?.data?.currentWeekType;
  const hasWeekB = hasWeekData(schedule?.weekB ?? []);
  const effectiveWeek = getDefaultWeek(currentWeekType, hasWeekB);
  const entries = effectiveWeek === 'A' ? (schedule?.weekA ?? []) : (schedule?.weekB ?? []);

  return (
    <div data-testid="eigen-rooster-widget" className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm">
      <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
        <h3 className="text-lg font-medium text-[#1E293B]">Mijn rooster deze week</h3>
        <Link to="/mijn-rooster" className="text-sm text-[#54949E] hover:underline">
          Bekijk alle →
        </Link>
      </div>

      <div className="p-6">
        {employeeId === undefined ? (
          <p className="text-sm text-gray-500">Geen medewerkerprofiel gekoppeld aan dit account.</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]"></div>
          </div>
        ) : isError ? (
          <p data-testid="eigen-rooster-error" className="text-sm text-[#D64545]">
            Kon je rooster niet laden. Probeer het later opnieuw.
          </p>
        ) : !hasWeekData(entries) ? (
          <p className="text-sm text-gray-500">Er is nog geen werkrooster ingevuld.</p>
        ) : (
          <div className="flex justify-between gap-2" data-testid="eigen-rooster-strook">
            {ORDERED_DAYS.map((dayOfWeek) => {
              const entry = getDayEntryForDay(entries, dayOfWeek);
              const isWorkday = !!entry?.isWorkday;
              return (
                <div key={dayOfWeek} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-gray-500">{DAY_LABELS[dayOfWeek]}</span>
                  <span
                    data-testid={isWorkday ? 'rooster-dag-werkt' : 'rooster-dag-vrij'}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      isWorkday ? 'bg-[#2F5A7A]/10 text-[#2F5A7A]' : 'bg-gray-100 text-gray-400'
                    }`}
                    title={isWorkday ? (entry?.location === 'kantoor' ? 'Kantoor' : 'Thuis') : 'Vrij'}
                  >
                    {isWorkday ? (entry?.location === 'kantoor' ? 'K' : 'T') : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
